import {
  PermissionCode,
  auditEntityTypeLabel,
  type AuditListQuery,
  type AuditLogItem,
} from '@gb-ticket/shared-types';
import { ChevronLeftIcon, ChevronRightIcon, Loader2Icon } from 'lucide-react';
import { useEffect, useState } from 'react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { usePageTitle } from '@/hooks/use-page-title';
import { hasPermission } from '@/platform/auth/permissions';
import { useMe } from '@/platform/auth/use-me';
import { PlatformPageHeader } from '@/platform/components/platform-page-header';
import { adminPanelClassName, adminTableFrameClassName } from '@/platform/features/admin-panel';
import { useAuditLog, useAuditLookups } from '@/platform/features/audit/use-audit';
import { ApiRequestError } from '@/lib/api';
import { formatDateTime } from '@/lib/format';

const EMPTY: AuditListQuery = { page: 1, pageSize: 50, sortOrder: 'desc' };

function endOfDayIso(date: string): string {
  return new Date(`${date}T23:59:59.999`).toISOString();
}

function startOfDayIso(date: string): string {
  return new Date(`${date}T00:00:00.000`).toISOString();
}

function jsonPreview(value: unknown): string | null {
  if (value == null) {
    return null;
  }
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return null;
  }
}

export function AuditPage() {
  usePageTitle('Audit-Log');
  const me = useMe();
  const allowed = hasPermission(me.data, PermissionCode.AUDIT_READ);
  const lookups = useAuditLookups(allowed);
  const [searchInput, setSearchInput] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [query, setQuery] = useState<AuditListQuery>(EMPTY);
  const [openId, setOpenId] = useState<string | null>(null);
  const log = useAuditLog(query, allowed);

  useEffect(() => {
    const handle = window.setTimeout(() => {
      setQuery((current) => {
        const next = searchInput.trim() || undefined;
        if (current.q === next) {
          return current;
        }
        return { ...current, q: next, page: 1 };
      });
    }, 300);
    return () => window.clearTimeout(handle);
  }, [searchInput]);

  function patch(patch: Partial<AuditListQuery>) {
    setQuery((current) => ({ ...current, ...patch, page: patch.page ?? 1 }));
  }

  const totalPages = log.data ? Math.max(1, Math.ceil(log.data.total / log.data.pageSize)) : 1;

  return (
    <section className="space-y-6">
      <PlatformPageHeader
        title="Audit-Log"
        description="Append-only Protokoll sicherheitsrelevanter Aktionen. Einträge können nicht geändert oder gelöscht werden."
      />

      <div
        className={`${adminPanelClassName} grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6`}
      >
        <div className="space-y-1 xl:col-span-2">
          <Label htmlFor="audit-q">Suche</Label>
          <Input
            id="audit-q"
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
            placeholder="Aktion, Nutzer, Entität, IP …"
          />
        </div>
        <div className="space-y-1">
          <Label>Nutzer</Label>
          <Select
            value={query.actorId ?? 'all'}
            onValueChange={(value) => patch({ actorId: value === 'all' ? undefined : value })}
          >
            <SelectTrigger>
              <SelectValue placeholder="Alle" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle</SelectItem>
              {(lookups.data?.actors ?? []).map((actor) => (
                <SelectItem key={actor.id} value={actor.id}>
                  {actor.displayName}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label>Aktion</Label>
          <Select
            value={query.action ?? 'all'}
            onValueChange={(value) => patch({ action: value === 'all' ? undefined : value })}
          >
            <SelectTrigger>
              <SelectValue placeholder="Alle" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle</SelectItem>
              {(lookups.data?.actions ?? []).map((item) => (
                <SelectItem key={item.code} value={item.code}>
                  {item.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label>Entität</Label>
          <Select
            value={query.entityType ?? 'all'}
            onValueChange={(value) => patch({ entityType: value === 'all' ? undefined : value })}
          >
            <SelectTrigger>
              <SelectValue placeholder="Alle" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle</SelectItem>
              {(lookups.data?.entityTypes ?? []).map((item) => (
                <SelectItem key={item.code} value={item.code}>
                  {item.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label htmlFor="audit-entity-id">Entitäts-ID</Label>
          <Input
            id="audit-entity-id"
            value={query.entityId ?? ''}
            onChange={(event) => patch({ entityId: event.target.value.trim() || undefined })}
            placeholder="UUID"
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="audit-from">Von</Label>
          <Input
            id="audit-from"
            type="date"
            value={fromDate}
            onChange={(event) => {
              const value = event.target.value;
              setFromDate(value);
              patch({ from: value ? startOfDayIso(value) : undefined });
            }}
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="audit-to">Bis</Label>
          <Input
            id="audit-to"
            type="date"
            value={toDate}
            onChange={(event) => {
              const value = event.target.value;
              setToDate(value);
              patch({ to: value ? endOfDayIso(value) : undefined });
            }}
          />
        </div>
      </div>

      {log.isPending && (
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2Icon className="size-4 animate-spin" aria-hidden />
          Audit-Log wird geladen …
        </p>
      )}
      {log.isError && (
        <p role="alert" className="text-sm text-destructive">
          {log.error instanceof ApiRequestError
            ? log.error.message
            : 'Das Audit-Log konnte nicht geladen werden.'}
        </p>
      )}

      {log.data && (
        <>
          <div className={adminTableFrameClassName}>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Zeit</TableHead>
                  <TableHead>Aktion</TableHead>
                  <TableHead>Nutzer</TableHead>
                  <TableHead>Entität</TableHead>
                  <TableHead>IP</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {log.data.items.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} className="text-muted-foreground">
                      Keine Einträge für diese Filter.
                    </TableCell>
                  </TableRow>
                )}
                {log.data.items.map((item) => (
                  <AuditRow
                    key={item.id}
                    item={item}
                    open={openId === item.id}
                    onToggle={() => setOpenId((current) => (current === item.id ? null : item.id))}
                  />
                ))}
              </TableBody>
            </Table>
          </div>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm text-muted-foreground">
              {log.data.total === 0
                ? 'Keine Einträge'
                : `${log.data.total} Einträge · Seite ${log.data.page} von ${totalPages}`}
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={query.page <= 1}
                onClick={() => patch({ page: query.page - 1 })}
              >
                <ChevronLeftIcon />
                Zurück
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={query.page >= totalPages}
                onClick={() => patch({ page: (query.page ?? 1) + 1 })}
              >
                Weiter
                <ChevronRightIcon />
              </Button>
            </div>
          </div>
        </>
      )}
    </section>
  );
}

function AuditRow({
  item,
  open,
  onToggle,
}: {
  item: AuditLogItem;
  open: boolean;
  onToggle: () => void;
}) {
  const before = jsonPreview(item.before);
  const after = jsonPreview(item.after);
  const metadata = jsonPreview(item.metadata);
  return (
    <>
      <TableRow className="cursor-pointer" onClick={onToggle}>
        <TableCell className="whitespace-nowrap">{formatDateTime(item.createdAt)}</TableCell>
        <TableCell>
          <span className="font-medium">{item.actionLabel}</span>
          <span className="mt-0.5 block font-mono text-xs text-muted-foreground">
            {item.action}
          </span>
        </TableCell>
        <TableCell>
          {item.actor ? (
            <>
              <span>{item.actor.displayName}</span>
              <span className="mt-0.5 block text-xs text-muted-foreground">{item.actor.email}</span>
            </>
          ) : (
            <span className="text-muted-foreground">System / unbekannt</span>
          )}
        </TableCell>
        <TableCell>
          <span>{auditEntityTypeLabel(item.entityType)}</span>
          <span className="mt-0.5 block font-mono text-xs text-muted-foreground">
            {item.entityId}
          </span>
        </TableCell>
        <TableCell className="font-mono text-xs text-muted-foreground">
          {item.ipAddress ?? '—'}
        </TableCell>
      </TableRow>
      {open && (
        <TableRow>
          <TableCell colSpan={5} className="bg-muted/40">
            <div className="grid gap-3 text-xs md:grid-cols-3">
              <DetailBlock title="Vorher" value={before} />
              <DetailBlock title="Nachher" value={after} />
              <DetailBlock
                title="Metadaten"
                value={metadata}
                extra={item.userAgent ? `User-Agent: ${item.userAgent}` : null}
              />
            </div>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

function DetailBlock({
  title,
  value,
  extra,
}: {
  title: string;
  value: string | null;
  extra?: string | null;
}) {
  return (
    <div>
      <p className="mb-1 font-medium">{title}</p>
      {extra ? <p className="mb-1 break-all text-muted-foreground">{extra}</p> : null}
      {value ? (
        <pre className="max-h-48 overflow-auto rounded-md border bg-background p-2 font-mono">
          {value}
        </pre>
      ) : (
        <p className="text-muted-foreground">—</p>
      )}
    </div>
  );
}
